import { useState } from "react";
import { useAuthQuery as useQuery } from '../../shared/hooks/useAuthQuery';
import { useMutation } from "convex/react";
import { api } from "../../convex/_generated/api";
import { Id } from "../../convex/_generated/dataModel";
import { Button } from "../../catalyst-components/button";
import { Dialog, DialogTitle, DialogDescription, DialogActions } from "../../catalyst-components/dialog";

type ActiveTab = "types" | "categories" | "tags" | "groups";

export default function KnowledgeBaseManagement() {
  const [activeTab, setActiveTab] = useState<ActiveTab>("types");

  return (
    <div className="space-y-6">
      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: "types" as const, label: "Types", icon: "📝" },
            { id: "categories" as const, label: "Categories", icon: "📁" },
            { id: "tags" as const, label: "Tags", icon: "🏷️" },
            { id: "groups" as const, label: "Groups", icon: "📦" },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap
                ${activeTab === tab.id
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }
              `}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-lg shadow p-6">
        {activeTab === "types" && <TypesManagement />}
        {activeTab === "categories" && <CategoriesManagement />}
        {activeTab === "tags" && <TagsManagement />}
        {activeTab === "groups" && <GroupsManagement />}
      </div>
    </div>
  );
}

// ============ TYPES MANAGEMENT ============

function TypesManagement() {
  const [isCreating, setIsCreating] = useState(false);
  const [editingId, setEditingId] = useState<Id<"knowledge_base_types"> | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [typeToDelete, setTypeToDelete] = useState<Id<"knowledge_base_types"> | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    displayName: "",
    description: "",
    icon: "",
    recommendedGranularity: "",
    isActive: true,
    sortOrder: "",
  });

  const types = useQuery(api.knowledgeBaseMeta.getAllTypes) || [];
  const createType = useMutation(api.knowledgeBaseMeta.createType);
  const updateType = useMutation(api.knowledgeBaseMeta.updateType);
  const deleteType = useMutation(api.knowledgeBaseMeta.deleteType);

  const handleSave = async () => {
    if (!formData.name || !formData.displayName) {
      alert("Name and display name are required");
      return;
    }

    const payload = {
      name: formData.name,
      displayName: formData.displayName,
      description: formData.description || undefined,
      icon: formData.icon || undefined,
      recommendedGranularity: formData.recommendedGranularity || undefined,
      isActive: formData.isActive,
      sortOrder: formData.sortOrder ? parseInt(formData.sortOrder) : undefined,
    };

    if (editingId) {
      await updateType({ id: editingId, ...payload });
      setEditingId(null);
    } else {
      await createType(payload);
      setIsCreating(false);
    }

    resetForm();
  };

  const handleEdit = (type: any) => {
    setEditingId(type._id);
    setFormData({
      name: type.name,
      displayName: type.displayName,
      description: type.description || "",
      icon: type.icon || "",
      recommendedGranularity: type.recommendedGranularity || "",
      isActive: type.isActive,
      sortOrder: type.sortOrder?.toString() || "",
    });
  };

  const resetForm = () => {
    setFormData({
      name: "",
      displayName: "",
      description: "",
      icon: "",
      recommendedGranularity: "",
      isActive: true,
      sortOrder: "",
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Content Types</h3>
        {!isCreating && !editingId && (
          <Button color="light" onClick={() => setIsCreating(true)}>
            Add Type
          </Button>
        )}
      </div>

      {(isCreating || editingId) && (
        <div className="border rounded-lg p-4 space-y-3 bg-gray-50">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">Name (internal)</label>
              <input
                type="text"
                className="w-full p-2 border rounded"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., faq"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Display Name</label>
              <input
                type="text"
                className="w-full p-2 border rounded"
                value={formData.displayName}
                onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                placeholder="e.g., FAQ"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Description</label>
            <textarea
              className="w-full p-2 border rounded"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={2}
            />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">Icon (emoji)</label>
              <input
                type="text"
                className="w-full p-2 border rounded"
                value={formData.icon}
                onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                placeholder="❓"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Sort Order</label>
              <input
                type="number"
                className="w-full p-2 border rounded"
                value={formData.sortOrder}
                onChange={(e) => setFormData({ ...formData, sortOrder: e.target.value })}
              />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
                <span className="text-sm font-medium">Active</span>
              </label>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Recommended Granularity</label>
            <input
              type="text"
              className="w-full p-2 border rounded"
              value={formData.recommendedGranularity}
              onChange={(e) => setFormData({ ...formData, recommendedGranularity: e.target.value })}
              placeholder="One Q&A per entry. Keep questions specific."
            />
          </div>

          <div className="flex gap-2">
            <Button color="light" onClick={handleSave}>Save</Button>
            <Button
              color="light"
              onClick={() => {
                setIsCreating(false);
                setEditingId(null);
                resetForm();
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {types.map((type) => (
          <div key={type._id} className="border rounded-lg p-4 flex justify-between items-start">
            <div>
              <div className="font-medium">
                {type.icon} {type.displayName} <span className="text-sm text-gray-500">({type.name})</span>
              </div>
              {type.description && <div className="text-sm text-gray-600 mt-1">{type.description}</div>}
              {type.recommendedGranularity && (
                <div className="text-xs text-blue-600 mt-1 italic">💡 {type.recommendedGranularity}</div>
              )}
              <div className="flex gap-2 mt-2">
                <span className={`text-xs px-2 py-1 rounded ${type.isActive ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"}`}>
                  {type.isActive ? "Active" : "Inactive"}
                </span>
                {type.sortOrder && (
                  <span className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-600">
                    Order: {type.sortOrder}
                  </span>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              <Button color="light" onClick={() => handleEdit(type)}>Edit</Button>
              <Button
                color="red"
                onClick={() => {
                  setTypeToDelete(type._id);
                  setDeleteDialogOpen(true);
                }}
              >
                Delete
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Type</DialogTitle>
        <DialogDescription>
          Are you sure you want to delete this type? This action cannot be undone.
        </DialogDescription>
        <DialogActions>
          <Button color="light" onClick={() => setDeleteDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            color="red"
            onClick={async () => {
              if (typeToDelete) {
                try {
                  await deleteType({ id: typeToDelete });
                  setDeleteDialogOpen(false);
                  setTypeToDelete(null);
                } catch (err: any) {
                  alert(err.message);
                }
              }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

// ============ CATEGORIES MANAGEMENT ============

function CategoriesManagement() {
  const [isCreating, setIsCreating] = useState(false);
  const [editingId, setEditingId] = useState<Id<"knowledge_base_categories"> | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<Id<"knowledge_base_categories"> | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    displayName: "",
    description: "",
    color: "#3b82f6",
    isActive: true,
  });

  const categories = useQuery(api.knowledgeBaseMeta.getAllCategories) || [];
  const createCategory = useMutation(api.knowledgeBaseMeta.createCategory);
  const updateCategory = useMutation(api.knowledgeBaseMeta.updateCategory);
  const deleteCategory = useMutation(api.knowledgeBaseMeta.deleteCategory);

  const handleSave = async () => {
    if (!formData.name || !formData.displayName) {
      alert("Name and display name are required");
      return;
    }

    const payload = {
      name: formData.name,
      displayName: formData.displayName,
      description: formData.description || undefined,
      color: formData.color || undefined,
      isActive: formData.isActive,
    };

    if (editingId) {
      await updateCategory({ id: editingId, ...payload });
      setEditingId(null);
    } else {
      await createCategory(payload);
      setIsCreating(false);
    }

    resetForm();
  };

  const handleEdit = (category: any) => {
    setEditingId(category._id);
    setFormData({
      name: category.name,
      displayName: category.displayName,
      description: category.description || "",
      color: category.color || "#3b82f6",
      isActive: category.isActive,
    });
  };

  const resetForm = () => {
    setFormData({
      name: "",
      displayName: "",
      description: "",
      color: "#3b82f6",
      isActive: true,
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Categories</h3>
        {!isCreating && !editingId && (
          <Button color="light" onClick={() => setIsCreating(true)}>
            Add Category
          </Button>
        )}
      </div>

      {(isCreating || editingId) && (
        <div className="border rounded-lg p-4 space-y-3 bg-gray-50">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">Name (internal)</label>
              <input
                type="text"
                className="w-full p-2 border rounded"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., getting-started"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Display Name</label>
              <input
                type="text"
                className="w-full p-2 border rounded"
                value={formData.displayName}
                onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                placeholder="e.g., Getting Started"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Description</label>
            <textarea
              className="w-full p-2 border rounded"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={2}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">Color (hex)</label>
              <div className="flex gap-2">
                <input
                  type="color"
                  className="h-10 w-20 border rounded"
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                />
                <input
                  type="text"
                  className="flex-1 p-2 border rounded"
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                />
              </div>
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
                <span className="text-sm font-medium">Active</span>
              </label>
            </div>
          </div>

          <div className="flex gap-2">
            <Button color="light" onClick={handleSave}>Save</Button>
            <Button
              color="light"
              onClick={() => {
                setIsCreating(false);
                setEditingId(null);
                resetForm();
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {categories.map((category) => (
          <div key={category._id} className="border rounded-lg p-4 flex justify-between items-start">
            <div>
              <div className="flex items-center gap-2">
                <div
                  className="w-4 h-4 rounded"
                  style={{ backgroundColor: category.color }}
                />
                <span className="font-medium">{category.displayName}</span>
                <span className="text-sm text-gray-500">({category.name})</span>
              </div>
              {category.description && <div className="text-sm text-gray-600 mt-1">{category.description}</div>}
              <div className="flex gap-2 mt-2">
                <span className={`text-xs px-2 py-1 rounded ${category.isActive ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"}`}>
                  {category.isActive ? "Active" : "Inactive"}
                </span>
                <span className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-600">
                  {category.usageCount} uses
                </span>
              </div>
            </div>
            <div className="flex gap-2">
              <Button color="light" onClick={() => handleEdit(category)}>Edit</Button>
              <Button
                color="red"
                onClick={() => {
                  setCategoryToDelete(category._id);
                  setDeleteDialogOpen(true);
                }}
              >
                Delete
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Category</DialogTitle>
        <DialogDescription>
          Are you sure you want to delete this category? This action cannot be undone.
        </DialogDescription>
        <DialogActions>
          <Button color="light" onClick={() => setDeleteDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            color="red"
            onClick={async () => {
              if (categoryToDelete) {
                await deleteCategory({ id: categoryToDelete });
                setDeleteDialogOpen(false);
                setCategoryToDelete(null);
              }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

// ============ TAGS MANAGEMENT ============

function TagsManagement() {
  const [isCreating, setIsCreating] = useState(false);
  const [editingId, setEditingId] = useState<Id<"knowledge_base_tags"> | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [tagToDelete, setTagToDelete] = useState<Id<"knowledge_base_tags"> | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    displayName: "",
    isActive: true,
  });

  const tags = useQuery(api.knowledgeBaseMeta.getAllTags) || [];
  const createTag = useMutation(api.knowledgeBaseMeta.createTag);
  const updateTag = useMutation(api.knowledgeBaseMeta.updateTag);
  const deleteTag = useMutation(api.knowledgeBaseMeta.deleteTag);

  const handleSave = async () => {
    if (!formData.name || !formData.displayName) {
      alert("Name and display name are required");
      return;
    }

    const payload = {
      name: formData.name,
      displayName: formData.displayName,
      isActive: formData.isActive,
    };

    try {
      if (editingId) {
        await updateTag({ id: editingId, ...payload });
        setEditingId(null);
      } else {
        await createTag(payload);
        setIsCreating(false);
      }
      resetForm();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleEdit = (tag: any) => {
    setEditingId(tag._id);
    setFormData({
      name: tag.name,
      displayName: tag.displayName,
      isActive: tag.isActive,
    });
  };

  const resetForm = () => {
    setFormData({
      name: "",
      displayName: "",
      isActive: true,
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Tags</h3>
        {!isCreating && !editingId && (
          <Button color="light" onClick={() => setIsCreating(true)}>
            Add Tag
          </Button>
        )}
      </div>

      {(isCreating || editingId) && (
        <div className="border rounded-lg p-4 space-y-3 bg-gray-50">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">Name (internal)</label>
              <input
                type="text"
                className="w-full p-2 border rounded"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., beginner"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Display Name</label>
              <input
                type="text"
                className="w-full p-2 border rounded"
                value={formData.displayName}
                onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                placeholder="e.g., Beginner"
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              />
              <span className="text-sm font-medium">Active</span>
            </label>
          </div>

          <div className="flex gap-2">
            <Button color="light" onClick={handleSave}>Save</Button>
            <Button
              color="light"
              onClick={() => {
                setIsCreating(false);
                setEditingId(null);
                resetForm();
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        {tags.map((tag) => (
          <div key={tag._id} className="border rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium">{tag.displayName}</span>
              <span className={`text-xs px-2 py-1 rounded ${tag.isActive ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"}`}>
                {tag.isActive ? "✓" : "✗"}
              </span>
            </div>
            <div className="text-xs text-gray-500 mb-2">{tag.name} • {tag.usageCount} uses</div>
            <div className="flex gap-2">
              <Button color="light" onClick={() => handleEdit(tag)}>Edit</Button>
              <Button
                color="red"
                onClick={() => {
                  setTagToDelete(tag._id);
                  setDeleteDialogOpen(true);
                }}
              >
                Delete
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Tag</DialogTitle>
        <DialogDescription>
          Are you sure you want to delete this tag? This action cannot be undone.
        </DialogDescription>
        <DialogActions>
          <Button color="light" onClick={() => setDeleteDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            color="red"
            onClick={async () => {
              if (tagToDelete) {
                await deleteTag({ id: tagToDelete });
                setDeleteDialogOpen(false);
                setTagToDelete(null);
              }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

// ============ GROUPS MANAGEMENT ============

function GroupsManagement() {
  const [isCreating, setIsCreating] = useState(false);
  const [editingId, setEditingId] = useState<Id<"knowledge_base_groups"> | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState<Id<"knowledge_base_groups"> | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    categoryId: "",
    isActive: true,
    sortOrder: "",
  });

  const groups = useQuery(api.knowledgeBaseMeta.getAllGroups) || [];
  const categories = useQuery(api.knowledgeBaseMeta.getActiveCategories) || [];
  const createGroup = useMutation(api.knowledgeBaseMeta.createGroup);
  const updateGroup = useMutation(api.knowledgeBaseMeta.updateGroup);
  const deleteGroup = useMutation(api.knowledgeBaseMeta.deleteGroup);

  const handleSave = async () => {
    if (!formData.name) {
      alert("Name is required");
      return;
    }

    const payload = {
      name: formData.name,
      description: formData.description || undefined,
      categoryId: formData.categoryId ? (formData.categoryId as Id<"knowledge_base_categories">) : undefined,
      isActive: formData.isActive,
      sortOrder: formData.sortOrder ? parseInt(formData.sortOrder) : undefined,
    };

    try {
      if (editingId) {
        await updateGroup({ id: editingId, ...payload });
        setEditingId(null);
      } else {
        await createGroup(payload);
        setIsCreating(false);
      }
      resetForm();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleEdit = (group: any) => {
    setEditingId(group._id);
    setFormData({
      name: group.name,
      description: group.description || "",
      categoryId: group.categoryId || "",
      isActive: group.isActive,
      sortOrder: group.sortOrder?.toString() || "",
    });
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      categoryId: "",
      isActive: true,
      sortOrder: "",
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Groups</h3>
        {!isCreating && !editingId && (
          <Button color="light" onClick={() => setIsCreating(true)}>
            Add Group
          </Button>
        )}
      </div>

      {(isCreating || editingId) && (
        <div className="border rounded-lg p-4 space-y-3 bg-gray-50">
          <div>
            <label className="block text-sm font-medium mb-1">Name</label>
            <input
              type="text"
              className="w-full p-2 border rounded"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Session Basics"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Description</label>
            <textarea
              className="w-full p-2 border rounded"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={2}
              placeholder="What entries does this group contain?"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">Category</label>
              <select
                className="w-full p-2 border rounded"
                value={formData.categoryId}
                onChange={(e) => setFormData({ ...formData, categoryId: e.target.value })}
              >
                <option value="">No category</option>
                {categories.map((cat) => (
                  <option key={cat._id} value={cat._id}>
                    {cat.displayName}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Sort Order</label>
              <input
                type="number"
                className="w-full p-2 border rounded"
                value={formData.sortOrder}
                onChange={(e) => setFormData({ ...formData, sortOrder: e.target.value })}
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              />
              <span className="text-sm font-medium">Active</span>
            </label>
          </div>

          <div className="flex gap-2">
            <Button color="light" onClick={handleSave}>Save</Button>
            <Button
              color="light"
              onClick={() => {
                setIsCreating(false);
                setEditingId(null);
                resetForm();
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {groups.map((group) => (
          <div key={group._id} className="border rounded-lg p-4 flex justify-between items-start">
            <div>
              <div className="font-medium">{group.name}</div>
              {group.description && <div className="text-sm text-gray-600 mt-1">{group.description}</div>}
              <div className="flex gap-2 mt-2">
                {group.categoryName && (
                  <span className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-800">
                    {group.categoryName}
                  </span>
                )}
                <span className={`text-xs px-2 py-1 rounded ${group.isActive ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"}`}>
                  {group.isActive ? "Active" : "Inactive"}
                </span>
                {group.sortOrder && (
                  <span className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-600">
                    Order: {group.sortOrder}
                  </span>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              <Button color="light" onClick={() => handleEdit(group)}>Edit</Button>
              <Button
                color="red"
                onClick={() => {
                  setGroupToDelete(group._id);
                  setDeleteDialogOpen(true);
                }}
              >
                Delete
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Group</DialogTitle>
        <DialogDescription>
          Are you sure you want to delete this group? Entries in this group won't be deleted, but they will no longer be grouped together.
        </DialogDescription>
        <DialogActions>
          <Button color="light" onClick={() => setDeleteDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            color="red"
            onClick={async () => {
              if (groupToDelete) {
                try {
                  await deleteGroup({ id: groupToDelete });
                  setDeleteDialogOpen(false);
                  setGroupToDelete(null);
                } catch (err: any) {
                  alert(err.message);
                }
              }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
